import React, { useCallback } from 'react';
import { formatInTimeZone } from 'date-fns-tz';

import Modal from '../BaseComponents/Modal';
import { ListNoteResponseElement } from '../../types/apiGeneratedTypes';
import { highlightTaggedUsersInHtml } from '../../utils/highlightTaggedUsersInHtml';

import './ViewNoteModal.scss';
import Button from '../BaseComponents/Button';

const MODAL_WIDTH = '520px';
const TIMEZONE = 'Europe/London';
const DATE_FORMAT = 'dd/MM/yyyy';
const TIME_FORMAT = 'h:mm a';

interface ViewNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  noteData: ListNoteResponseElement | null;
  isLoading?: boolean;
  isAlertOpenNote?: boolean;
  onMarkUnread?: () => void;
}

const ViewNoteModal: React.FC<ViewNoteModalProps> = ({
  isOpen,
  onClose,
  noteData,
  isLoading = false,
  isAlertOpenNote = false,
  onMarkUnread
}) => {
  const formatDate = useCallback((dateString: string): string => {
    try {
      return formatInTimeZone(new Date(dateString), TIMEZONE, DATE_FORMAT);
    } catch {
      return 'Invalid Date';
    }
  }, []);

  const formatTime = useCallback((dateString: string): string => {
    try {
      return formatInTimeZone(new Date(dateString), TIMEZONE, TIME_FORMAT);
    } catch {
      return 'Invalid Time';
    }
  }, []);

  if (!noteData && !isLoading) return null;

  const renderLoadingState = () => (
    <div className="loading-container">
      <div className="loading-spinner" />
      <p>Loading note details...</p>
    </div>
  );

  const renderNoteContent = () => {
    if (!noteData) return null;

    return (
      <>
        <div className="note-meta-info">
          <span className="note-date">{formatDate(noteData.created_at)}</span>
          <span className="note-time">{formatTime(noteData.created_at)}</span>
          <span className="note-author">
            {`${noteData.author.first_name} ${noteData.author.last_name[0]}.`}
          </span>
        </div>

        <div className="note-section">
          <div className="label-title">Title</div>
          <div className="note-title-display">
            {noteData.title || 'No title'}
          </div>
        </div>

        <div className="note-section">
          <div className="label-title">Note</div>
          <div
            className="note-description-display"
            dangerouslySetInnerHTML={{
              __html: highlightTaggedUsersInHtml(noteData.description)
            }}
          />
        </div>
        {isAlertOpenNote && (
          <div className='btn-wrapper'>
            <Button
              text='Mark as unread'
              onClick={onMarkUnread}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="View Note"
      width={MODAL_WIDTH}
    >
      <div className="view-note-modal">
        {isLoading ? renderLoadingState() : renderNoteContent()}
      </div>
    </Modal>
  );
};

export default ViewNoteModal;
